import { Button, Table, useCreateColumnHelper } from '@core/ui';
import { DateTime } from 'luxon';
import { getI18n, useTranslation } from 'react-i18next';

export interface Contact {
    emailAddress: string;
    nickname: string;
    firstName?: string;
    lastName?: string;
    companyName?: string;
    country?: string;
    timestamp?: string;
    firstOnline?: string;
    lastOnline?: string;
    timeOnline?: string;
    percentageOnline?: number;
}

interface Props {
    exportTitle?: string;
    exportFileName?: string;
    webinarTitle: string;
    showTimestamp?: boolean;
    showTimeOnline?: boolean;
    contacts: Contact[];
}

export const ContactList = ({ contacts, showTimeOnline, showTimestamp, exportTitle, exportFileName, webinarTitle }: Props) => {
    const { t } = useTranslation();
    const columnHelper = useCreateColumnHelper<Contact>();
    const columns = [
        columnHelper.accessor((row) => row.emailAddress, {
            id: 'emailAddress',
            cell: (info) => <>{info.getValue()}</>,
            header: () => <>{t('Account.Label.Email')}</>
        }),
        columnHelper.accessor((row) => row.nickname, {
            id: 'nickname',
            cell: (info) => <>{info.getValue()}</>,
            header: () => <>{t('Account.Label.Nickname')}</>
        }),
        columnHelper.accessor((row) => row.firstName, {
            id: 'firstName',
            cell: (info) => <>{info.getValue()}</>,
            header: () => <>{t('Account.Label.FirstName')}</>
        }),
        columnHelper.accessor((row) => row.lastName, {
            id: 'lastName',
            cell: (info) => <>{info.getValue()}</>,
            header: () => <>{t('Account.Label.LastName')}</>
        }),
        columnHelper.accessor((row) => row.companyName, {
            id: 'companyName',
            cell: (info) => <>{info.getValue()}</>,
            header: () => <>{t('Account.Label.CompanyName')}</>
        }),
        columnHelper.accessor((row) => row.country, {
            id: 'country',
            cell: (info) => <>{info.getValue()}</>,
            header: () => <>{t('Account.Label.Country')}</>
        })
    ];

    if (showTimestamp) {
        columns.push(
            columnHelper.accessor((row) => row.timestamp, {
                id: 'timestamp',
                cell: (info) => {
                    const value = info.getValue();
                    return <>{value ? DateTime.fromISO(value).toLocal().setLocale(getI18n().language).toLocaleString(DateTime.DATETIME_SHORT) : ''}</>;
                },
                header: () => <>{t('ContactList.Table.Header.Date')}</>
            })
        );
    }

    if (showTimeOnline) {
        columns.push(
            columnHelper.accessor((row) => row.timeOnline, {
                id: 'timeOnline',
                cell: (info) => {
                    const value = info.getValue();
                    return <>{value ? (value.indexOf('.') !== -1 ? value.split('.')[0] : value) : ''}</>;
                },
                header: () => <>{t('ContactList.Table.Header.TimeOnline')}</>
            })
        );
        columns.push(
            columnHelper.accessor((row) => (row.percentageOnline ? row.percentageOnline.toString() : null), {
                id: 'percentageOnline',
                cell: (info) => {
                    const value = info.getValue();
                    return <>{value ? `${value}%` : ''}</>;
                },
                header: () => <>{t('ContactList.Table.Header.PercentageOnline')}</>
            })
        );
        columns.push(
            columnHelper.accessor((row) => row.firstOnline, {
                id: 'firstOnline',
                cell: (info) => {
                    const value = info.getValue();
                    return <>{value ? DateTime.fromISO(value).toLocal().setLocale(getI18n().language).toLocaleString(DateTime.DATETIME_SHORT) : ''}</>;
                },
                header: () => <>{t('ContactList.Table.Header.FirstOnline')}</>
            })
        );
    }

    if (!contacts || contacts.length === 0) {
        return <></>;
    }

    const exportExcelAsync = async () => {
        const xlsx = await import('xlsx');
        const workbook = xlsx.utils.book_new();
        workbook.Props = {
            Application: 'OnlineWebinar',
            AppVersion: '1.0.0',
            Author: 'OnlineWebinar',
            CreatedDate: new Date(),
            Title: exportTitle
        };

        // prettier-ignore
        const summaryData: (string | Date | number | undefined)[][] = [
            [exportTitle + ' for ' + webinarTitle],
            [],
            ['Exported on', , , , new Date()],
            [],
        ];
        const header = ['First name', 'Last name', 'Nickname', 'Email address', 'Company', 'Country'];
        if (showTimestamp) {
            header.push('Date');
        }
        if (showTimeOnline) {
            header.push('Time online');
            header.push('%');
            header.push('First online');
        }
        summaryData.push(header);
        contacts.forEach((contact) => {
            const row: (string | Date | number | undefined)[] = [
                contact.firstName,
                contact.lastName,
                contact.nickname,
                contact.emailAddress,
                contact.companyName,
                contact.country
            ];
            if (showTimestamp) {
                row.push(contact.timestamp ? new Date(contact.timestamp) : undefined);
            }
            if (showTimeOnline) {
                row.push(contact.timeOnline ? (contact.timeOnline.indexOf('.') !== -1 ? contact.timeOnline.split('.')[0] : contact.timeOnline) : undefined);
                row.push(contact.percentageOnline);
                row.push(contact.firstOnline ? new Date(contact.firstOnline) : undefined);
            }
            summaryData.push(row);
        });

        const summary = xlsx.utils.aoa_to_sheet(summaryData);
        xlsx.utils.book_append_sheet(workbook, summary, 'Summary');

        xlsx.writeFileXLSX(workbook, exportFileName || 'ContactList.xlsx');
    };

    const exportExcel = () => {
        exportExcelAsync().then(() => {
            // Done...
        });
    };

    return (
        <>
            {exportFileName && (
                <div>
                    <Button variant="primary" onClick={exportExcel} classNames="mb-4">
                        {t('ContactList.Button.Export')}
                    </Button>
                </div>
            )}
            <div className="bg-white rounded-lg shadow-md">
                <Table columns={columns} data={contacts} isHeader={true} />
            </div>
        </>
    );
};
